import {
    Box,
    Checkbox,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    FormControl,
    Select,
    SelectChangeEvent,
    ListSubheader,
    Divider,
} from '@mui/material'
import React, { useId } from 'react'
import { DropdownItem } from './DropdownItem'

export interface DropdownSelectProps {
    disabled?: boolean
    label?: string
    items: readonly DropdownItem[] | Record<string, DropdownItem[]>
    isSlim?: boolean
    selectedItems: string[] | string
    onChange: (value: string[]) => void
    multiple?: boolean
}

const getLabel = ({
    multiple,
    selected,
    items,
}: {
    multiple?: boolean
    items: DropdownSelectProps['items']
    selected: string[] | string
}) => {
    if (!multiple) {
        if (Array.isArray(items)) {
            return items.find((i) => i.id === selected[0])?.label
        }

        return Object.values(items)
            .flat()
            .find((i) => i.id === selected[0])?.label
    }

    if (!selected.length || selected.length === items.length) {
        return 'Visa samtliga'
    }

    if (selected.length === 1) return '1 vald'

    return selected.length + ' valda'
}

const DropdownSelect = ({ disabled, label, items, isSlim, selectedItems, onChange, multiple }: DropdownSelectProps) => {
    const labelId = useId()
    const handleChange = (event: SelectChangeEvent<string | string[]>) => {
        if (multiple) {
            onChange(event.target.value as string[])
        } else {
            onChange([event.target.value as string])
        }
    }

    const renderItems = (i: DropdownItem[]) =>
        i.map((item) => (
            <MenuItem key={item.id} dense sx={{ paddingTop: 0, paddingBottom: 0 }} value={item.id}>
                {multiple && <Checkbox checked={selectedItems.includes(item.id)} />}
                <ListItemText primary={item.label} />
            </MenuItem>
        ))

    return (
        <FormControl fullWidth={!isSlim}>
            {label && (
                <InputLabel id={labelId} shrink>
                    {label}
                </InputLabel>
            )}
            <Select
                fullWidth={!isSlim}
                labelId={labelId}
                disabled={disabled}
                displayEmpty
                multiple={multiple}
                value={selectedItems}
                onChange={handleChange}
                input={<OutlinedInput notched label={label} />}
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getLabel({ multiple, selected, items })}
                    </Box>
                )}
                size={isSlim ? 'small' : undefined}
                sx={{ paddingTop: 0 }}
            >
                {Array.isArray(items)
                    ? renderItems(items)
                    : Object.keys(items).map((header, index, arr) => [
                          <ListSubheader key={header} sx={{ lineHeight: '32px' }}>
                              {header}
                          </ListSubheader>,
                          renderItems(items[header]),
                          index < arr.length - 1 && <Divider sx={{ marginTop: 4 }} />,
                      ])}
            </Select>
        </FormControl>
    )
}

export default DropdownSelect
